import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '../../services/app.service';
import { HttpService } from '../../services/http.service';
import { PermissionService } from '../../services/permission.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { UrlService } from '../../services/url.service';
import { environment } from '../../../../environments/environment';
import { PrintTypeLong } from '../../models/print-type';
import { UploadAttachmentControl } from '../documents-selection/documents-selection.component';

const EMAIL_SEPERATOR = /[,;]/; // separate by "," or ";"

function commaSeparatedEmailsValidator(control: AbstractControl): ValidationErrors {
    const errors = (<string>control.value)
        .split(EMAIL_SEPERATOR)
        .map((email) => Validators.email(<AbstractControl>{ value: email.trim() }))
        .filter((v) => v !== null);
    if (errors.length) {
        return { email: true };
    }
    return null;
}

export const atLeastOneValidator = (keys: string[]) => {
    return (group: FormGroup) => {
        const { controls } = group;
        return keys.some((key) => {
            const value = controls[key] && controls[key].value;
            return value && (!Array.isArray(value) || value.length > 0);
        })
            ? null
            : { atLeastOne: 'error' };
    };
};

@Component({
    selector: 'app-send-documents-via-email-dialog',
    templateUrl: './send-documents-via-email-dialog.component.html',
    styleUrls: ['./send-documents-via-email-dialog.component.scss'],
})
export class SendDocumentsViaEmailDialogComponent implements OnInit {
    public sendEmailForm = new FormGroup(
        {
            emailSelect: new FormControl<string>('', []),
            email: new FormControl<string>('', [commaSeparatedEmailsValidator]),
            emailCC: new FormControl<string>('', [commaSeparatedEmailsValidator]),
            emailBCC: new FormControl<string>('', [commaSeparatedEmailsValidator]),
            emailBody: new FormControl<string>(''),
        },
        { validators: atLeastOneValidator(['emailSelect', 'email']) }
    );
    public isLoading = false;
    public sanitizedHtml: SafeHtml;
    constructor(
        @Inject(MAT_DIALOG_DATA) public data: SendDocumentSettings,
        public dialogRef: MatDialogRef<SendDocumentsViaEmailDialogComponent>,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private permissionService: PermissionService,
        private urlService: UrlService,
        private appService: AppService,
        private httpService: HttpService,
        private sanitizer: DomSanitizer
    ) {}

    public ngOnInit() {
        if (this.permissionService.userInfo$.value?.mail) {
            this.sendEmailForm.controls.emailCC.setValue(this.permissionService.userInfo$.value.mail);
        }
        this.sanitizedHtml = this.sanitizer.bypassSecurityTrustHtml(this.data.mailText || '');
    }

    public onNoClick() {
        this.dialogRef.close();
    }

    public onYesClick() {
        this.isLoading = true;
        this.sendMail();
    }

    private setControlsEnabled(enabled: boolean) {
        Object.values(this.sendEmailForm.controls).forEach((control) => {
            if (enabled) {
                control.enable();
            } else {
                control.disable();
            }
        });
    }

    public isEmployee() {
        return this.permissionService.isUserAnEmployee;
    }

    private sendMail() {
        this.setControlsEnabled(false);
        const { emailSelect, email, emailCC, emailBCC, emailBody } = <
            { emailSelect: string; email: string; emailCC: string; emailBCC: string; emailBody: string }
        >this.sendEmailForm.value;
        const toEmailList = (mail: string) =>
            mail
                .split(EMAIL_SEPERATOR)
                .map((v) => v.trim())
                .filter((v) => !!v);
        const mails = toEmailList(email);

        const mergeEmailLists = (originalList, additionalList) =>
            [...new Set([...originalList, ...additionalList])].filter((v) => !!v);
        this.sendMailWithDocuments({
            emails: mergeEmailLists(mails, emailSelect),
            emailsCC: toEmailList(emailCC),
            emailsBCC: toEmailList(emailBCC),
            emailBody,
        }).subscribe(
            () => {
                this.snackBarService.openSnackBar({
                    message: this.translateService.instant('NEXT_STEPS.EMAIL_DIALOG.SENDING_DOCUMENTS.SUCCESS_MSG'),
                    isFailure: false,
                });
                this.dialogRef.close();
                this.isLoading = false;
                this.setControlsEnabled(true);
            },
            () => {
                this.snackBarService.openSnackBar({
                    message: this.translateService.instant('NEXT_STEPS.EMAIL_DIALOG.SENDING_DOCUMENTS.FAILURE_MSG'),
                    isFailure: true,
                });
                this.isLoading = false;
                this.setControlsEnabled(true);
            }
        );
    }

    public sendMailWithDocuments({
        emails,
        emailsCC,
        emailsBCC,
        emailBody,
    }: {
        emails: string[];
        emailsCC: string[];
        emailsBCC: string[];
        emailBody: string;
    }) {
        const id = this.data.quoteId;
        const url = this.data.isLegacy
            ? this.urlService.getUrl('sendMailWithDocuments', { id })
            : `${environment.http.quotation}quotations/quote-assist-pro/quotes/${id}/mail`;

        // when legacy is removed, change data.offerAttachments to string[] and pass documentUris
        const archiveDocumentUris = this.data.offerAttachments.map((oa) => oa.documentUri);
        const payload = {
            emailReceivers: emails,
            emailCCReceivers: emailsCC,
            emailBCCReceivers: emailsBCC,
            emailBody,
            exportFormats: this.data.selectedExportFormats,
            language: this.appService.getLanguageOnly(),
            country: this.appService.getLocation(),
            schemes: this.data.schemes,
            schemesDwt: this.data.schemesDwt,
            ...(this.data.energyLabelSelected && { energyLabel: this.data.energyLabel }),
            ...(this.data.isLegacy && {
                salesOrganization: this.appService.getSalesOrg(),
                sendEmailToSalesRep: false,
                offerAttachments: this.data.offerAttachments,
            }),
            ...(!this.data.isLegacy && {
                salesOrg: this.appService.getSalesOrg(),
                printType: this.data.printType,
                archiveDocumentUris,
            }),
        };

        return this.httpService.post(url, payload);
    }
}

export interface SendDocumentSettings {
    selectedExportFormats: string[];
    energyLabelSelected: boolean;
    energyLabel: string;
    schemes: string[];
    schemesDwt: string[];
    quoteId: string;
    offerAttachments: UploadAttachmentControl[];
    mailText: string;
    mailRecipients: string[];
    printType: PrintTypeLong;
    isLegacy: boolean;
}
